import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ownerRollup, dailyBrief, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Buildings, CurrencyDollar, ShoppingCart, ChartLineUp, Target, ChartBar, ListChecks,
  CalendarStar, Package, Sparkle, ArrowRight, Sun, ArrowsClockwise, Coffee, CheckCircle,
} from "@phosphor-icons/react";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-matte p-4" data-testid={`owner-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 label-caps"><Icon size={13} className={accent ? "brand-text" : "text-[#9a9a9a]"} weight="bold" /> {label}</div>
      <div className="font-head text-xl md:text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

function BrandCard({ b, onView, active }) {
  const k = b.kpis;
  return (
    <div className={`card-matte p-5 ${active ? "border-[#6B3DFF]/50 ring-1 ring-[#6B3DFF]/40" : ""}`} data-testid={`brand-card-${b.client_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#6B3DFF]/15 flex items-center justify-center"><Coffee size={17} className="brand-text" weight="fill" /></div>
          <div className="font-head font-semibold">{b.name}</div>
          {active && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#6B3DFF]/15 text-[#A56CFF]">Active</span>}
        </div>
        <button data-testid={`view-brand-${b.client_id}`} onClick={() => onView(b.client_id)} className="text-xs brand-text hover:text-[#A56CFF] flex items-center gap-1">Open <ArrowRight size={12} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[["Revenue", fmtMoney(k.revenue)], ["Orders", fmtNum(k.orders)], ["Traffic", fmtNum(k.traffic)],
          ["CVR", fmtPct(k.conversion_rate)], ["ROAS", `${k.roas}x`], ["Inventory", fmtNum(k.inventory_alerts)]].map(([l, v]) => (
          <div key={l} className="bg-white/[0.03] border border-white/8 rounded-lg px-2 py-1.5"><div className="text-[10px] uppercase text-[#7a7a7a]">{l}</div><div className="text-sm font-semibold text-[#cfcfcf]">{v}</div></div>
        ))}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-[#9a9a9a] mb-3">
        <span className="flex items-center gap-1"><ListChecks size={12} className="brand-text" /> {k.pending_approvals} pending</span>
        <span className="flex items-center gap-1"><CalendarStar size={12} className="brand-text" /> {k.scheduled_campaigns} scheduled</span>
      </div>
      <div className="label-caps mb-1.5">AI Recommendations</div>
      <ul className="space-y-1">
        {(b.recommendations || []).slice(0, 3).map((r, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs text-[#cfcfcf]"><Sparkle size={11} className="brand-text mt-0.5 shrink-0" weight="fill" />{r}</li>
        ))}
      </ul>
    </div>
  );
}

function IndividualBrand({ brand }) {
  const k = brand.kpis;
  const briefQ = useQuery({ queryKey: ["daily-brief", brand.client_id], queryFn: () => dailyBrief(brand.client_id, "Tom"), staleTime: 10 * 60 * 1000 });
  const b = briefQ.data;
  return (
    <div data-testid="individual-brand-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Kpi label="Revenue" value={fmtMoney(k.revenue)} icon={CurrencyDollar} accent />
        <Kpi label="Orders" value={fmtNum(k.orders)} icon={ShoppingCart} />
        <Kpi label="Traffic" value={fmtNum(k.traffic)} icon={ChartLineUp} accent />
        <Kpi label="Conversion Rate" value={fmtPct(k.conversion_rate)} icon={Target} />
        <Kpi label="ROAS" value={`${k.roas}x`} icon={ChartBar} accent />
        <Kpi label="Pending Approvals" value={fmtNum(k.pending_approvals)} icon={ListChecks} />
        <Kpi label="Scheduled Campaigns" value={fmtNum(k.scheduled_campaigns)} icon={CalendarStar} accent />
        <Kpi label="Inventory Alerts" value={fmtNum(k.inventory_alerts)} icon={Package} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-matte p-5" data-testid="brand-daily-brief">
          <div className="flex items-center gap-2 mb-2"><Sun size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold">Daily Executive Brief</span></div>
          {briefQ.isLoading || briefQ.isFetching ? (
            <div className="text-sm text-[#9a9a9a] ai-thinking">Generating brief for {brand.name}…</div>
          ) : b ? (
            <>
              <p className="text-sm text-[#cfcfcf] leading-relaxed">{b.summary}</p>
              <div className="label-caps mt-4 mb-2">Recommended Actions</div>
              <div className="space-y-2">
                {(b.recommended_actions || []).slice(0, 4).map((r, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/8">
                    <div className="text-sm font-semibold text-[#cfcfcf]">{r.action}</div>
                    <div className="text-xs text-[#9a9a9a] mt-0.5">{r.why}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] text-[#7a7a7a] flex items-center gap-1.5"><CheckCircle size={13} className="text-emerald-400" /> Recommendation-only — nothing publishes automatically.</div>
            </>
          ) : <div className="text-sm text-[#7a7a7a]">Could not load brief.</div>}
        </div>

        <div className="card-matte p-5" data-testid="brand-calendar">
          <div className="flex items-center gap-2 mb-3"><CalendarStar size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold">Marketing Calendar</span></div>
          {(brand.scheduled_items || []).length ? (
            <div className="space-y-2">
              {brand.scheduled_items.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2">
                  <span className="text-sm text-[#cfcfcf] truncate max-w-[180px]">{s.title}</span>
                  <span className="text-xs text-sky-400">{s.scheduled_at}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-[#7a7a7a]">No scheduled content. Schedule items from the Approval Queue.</div>}
          <div className="label-caps mt-5 mb-2">Best Sellers</div>
          <div className="flex flex-wrap gap-1.5">
            {(brand.best_sellers || []).map((p, i) => <span key={i} className="text-xs text-[#cfcfcf] bg-white/[0.03] border border-white/8 px-2 py-0.5 rounded">{p}</span>)}
            {!(brand.best_sellers || []).length && <span className="text-xs text-[#7a7a7a]">—</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OwnerDashboardPage() {
  const { activeId, setActive } = useClient();
  const navigate = useNavigate();
  const [view, setView] = useState("combined");

  const q = useQuery({ queryKey: ["owner-rollup"], queryFn: ownerRollup });
  const data = q.data;
  const brands = data?.brands || [];
  const c = data?.combined;
  const selected = brands.find((b) => b.client_id === activeId) || brands[0];

  // Open a business: sync the global workspace + go to its Executive Dashboard
  const openBusiness = (id) => { setActive(id); navigate("/"); };

  return (
    <PageContainer>
      <PageHeader eyebrow="Owner" title="Multi-Brand Executive Roll-Up"
        subtitle="All your brands in one view. Read-only analytics & AI recommendations — no publishing actions here.">
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-white/5 p-1">
            <button data-testid="view-combined" onClick={() => setView("combined")} className={`px-3 py-1.5 rounded-md text-sm ${view === "combined" ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>Combined Business</button>
            <button data-testid="view-individual" onClick={() => setView("individual")} className={`px-3 py-1.5 rounded-md text-sm ${view === "individual" ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>Individual Brand</button>
          </div>
          <button data-testid="owner-refresh" onClick={() => q.refetch()} className="text-[#9a9a9a] hover:text-white p-2"><ArrowsClockwise size={16} className={q.isFetching ? "ai-thinking" : ""} /></button>
        </div>
      </PageHeader>

      {view === "combined" ? (
        <>
          <div className="flex items-center gap-2 mb-3"><Buildings size={16} className="brand-text" weight="fill" /><span className="label-caps">Combined totals · {c?.brand_count || 0} brands</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="combined-totals">
            <Kpi label="Total Revenue" value={fmtMoney(c?.revenue)} icon={CurrencyDollar} accent />
            <Kpi label="Total Orders" value={fmtNum(c?.orders)} icon={ShoppingCart} />
            <Kpi label="Total Traffic" value={fmtNum(c?.traffic)} icon={ChartLineUp} accent />
            <Kpi label="Blended ROAS" value={`${c?.roas || 0}x`} icon={ChartBar} />
            <Kpi label="Avg Conv. Rate" value={fmtPct(c?.conversion_rate)} icon={Target} accent />
            <Kpi label="Pending Approvals" value={fmtNum(c?.pending_approvals)} icon={ListChecks} />
            <Kpi label="Scheduled Campaigns" value={fmtNum(c?.scheduled_campaigns)} icon={CalendarStar} accent />
            <Kpi label="Inventory Alerts" value={fmtNum(c?.inventory_alerts)} icon={Package} />
          </div>

          <div className="card-matte p-5 mb-7" data-testid="combined-revenue-chart">
            <div className="label-caps mb-3">Combined Revenue (7d, all brands)</div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={c?.revenue_series || []}>
                <defs><linearGradient id="ownerRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" /><XAxis dataKey="date" tick={{ fill: "#7a7a7a", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} /><YAxis tick={{ fill: "#7a7a7a", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#14141F", border: "1px solid #ffffff20", borderRadius: 8 }} />
                <Area type="monotone" dataKey="revenue" stroke="#6B3DFF" strokeWidth={2.5} fill="url(#ownerRev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="label-caps mb-3">Brands</div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="brand-grid">
            {brands.map((b) => <BrandCard key={b.client_id} b={b} onView={openBusiness} active={b.client_id === activeId} />)}
          </div>
        </>
      ) : (
        <>
          <div className="mb-5 flex items-center gap-3">
            <select data-testid="brand-select" value={selected?.client_id || ""} onChange={(e) => setActive(e.target.value)} className="bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#cfcfcf]">
              {brands.map((b) => <option key={b.client_id} value={b.client_id}>{b.name}</option>)}
            </select>
            {selected && <button data-testid="open-business-dashboard" onClick={() => openBusiness(selected.client_id)} className="text-xs brand-text hover:text-[#A56CFF] flex items-center gap-1">Open full dashboard <ArrowRight size={12} /></button>}
          </div>
          {selected ? <IndividualBrand brand={selected} /> : <div className="text-sm text-[#7a7a7a]">No brands found.</div>}
        </>
      )}
    </PageContainer>
  );
}
